import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Music } from 'lucide-react';
import { WaterfallDisplay } from './WaterfallDisplay';
import { NotationDisplay } from './NotationDisplay';
import { PianoKeyboard } from './PianoKeyboard';
import { PlaybackControls } from './PlaybackControls';
import { PracticeSettings } from './PracticeSettings';
import { useScoreStore, usePlaybackStore, useMIDIStore } from '@/stores';
import { useNotationPanelResize } from '@/hooks/useNotationPanelResize';
import type { SkipContext } from '@/stores/playbackStore';
import { useMIDIInput } from '@/hooks/useMIDIInput';
import { usePianoAudio } from '@/hooks/usePianoAudio';
import { usePlaybackEngine } from '@/hooks/usePlaybackEngine';
import { useAutoPlay } from '@/hooks/useAutoPlay';
import { useExpectedNotes } from '@/hooks/useExpectedNotes';

const KEYBOARD_HEIGHT = 140;
const KEYBOARD_HEIGHT_COMPACT = 90;
const CONTROLS_HEIGHT = 52;
const WATERFALL_MIN_HEIGHT = 300; // waterfall's fallback floor (also used as the drag cap)

/**
 * OnlinePianist-style practice view.
 *
 * Default layout (OTO-28: notation panel ON by default):
 * ┌─────────────────────────────────────────┐
 * │       WATERFALL (remaining space)        │
 * │  Green bars (LH) + Blue bars (RH)       │
 * │  Notes fall toward keyboard              │
 * ├────────────── drag handle ──────────────┤
 * │  NOTATION (sheet music) — height        │
 * │  120–500px, drag-resizable, persisted   │
 * ├─────────────────────────────────────────┤
 * │     PIANO KEYBOARD (compact, 90px)       │
 * ├─────────────────────────────────────────┤
 * │  Controls: play, seek, speed, settings   │
 * └─────────────────────────────────────────┘
 *
 * The Notation toggle (overlay, top-left of the waterfall) is kept: it
 * hides the panel, restoring the full-height waterfall and full-size
 * keyboard. The open/closed state is intentionally NOT persisted; the
 * panel height is (uiStore, localStorage key 'otoflow-ui', committed once
 * per drag gesture by useNotationPanelResize).
 */
export function PracticeView() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [showNotation, setShowNotation] = useState(true); // OTO-28: on by default; toggle kept

  // Store data
  const currentScore = useScoreStore((state) => {
    const scores = state.scores;
    const id = state.currentScoreId;
    return scores.find((s) => s.id === id) || null;
  });
  const fetchTimeline = useScoreStore((state) => state.fetchTimeline);
  const timeline = currentScore?.timeline;

  // OTO-21: granular selectors only (was a whole `state.position` object,
  // re-rendering this entire view at 60fps). `state` and `config` are
  // already low-frequency (user-triggered changes only); per-frame reads
  // (currentTime) now live in the leaf components/hooks that actually need
  // them (WaterfallDisplay, NotationDisplay, useAutoPlay, useExpectedNotes).
  const playbackState = usePlaybackStore((state) => state.state);
  const config = usePlaybackStore((state) => state.config);

  const activeNotes = useMIDIStore((state) => state.activeNotes);
  const { notationHeight, onHandleMouseDown, isResizing } = useNotationPanelResize({
    maxFromLayout:
      dimensions.height > 0
        ? dimensions.height - KEYBOARD_HEIGHT_COMPACT - CONTROLS_HEIGHT - WATERFALL_MIN_HEIGHT
        : null,
  });

  // Initialize hooks
  useMIDIInput();
  const {
    playNote,
    stopNote,
    stopAllNotes,
    cancelScheduled,
    isLoaded: audioLoaded,
    loadState: pianoLoadState,
    retry: retryPianoLoad,
  } = usePianoAudio();
  usePlaybackEngine(timeline);
  useAutoPlay(timeline, { playNote, stopNote, stopAllNotes, cancelScheduled });
  const { pitches: expectedNotes, staffByPitch: expectedStaffByPitch } = useExpectedNotes(timeline);

  // OTO-027: config.loopStart/loopEnd are measure numbers, not seconds -- the
  // playbackStore has no timeline access to convert them, so PlaybackControls'
  // ±5s skipBy() clamp bounds are resolved here, mirroring usePlaybackEngine's
  // loop-bounds resolution verbatim (same `|| fallback` expressions) so skip
  // clamping can never disagree with the engine's own loop-wrap behavior.
  const skipContext = useMemo<SkipContext>(() => {
    if (!timeline) {
      return { totalDuration: 0, loopStartTime: null, loopEndTime: null, chords: [] };
    }
    let loopStartTime: number | null = null;
    let loopEndTime: number | null = null;
    if (config.loopStart !== null && config.loopEnd !== null) {
      loopStartTime = timeline.measures.find((m) => m.number === config.loopStart)?.startTime || 0;
      loopEndTime =
        timeline.measures.find((m) => m.number === config.loopEnd)?.startTime ||
        timeline.totalDuration;
    }
    return {
      totalDuration: timeline.totalDuration,
      loopStartTime,
      loopEndTime,
      chords: timeline.chords,
    };
  }, [timeline, config.loopStart, config.loopEnd]);

  const resolveNotationSkipContext = useCallback((): SkipContext => skipContext, [skipContext]);

  // Fetch timeline on-demand if not loaded yet
  useEffect(() => {
    if (
      currentScore &&
      !currentScore.timeline &&
      (currentScore.status === 'ready' || currentScore.status === 'needs_correction')
    ) {
      fetchTimeline(currentScore.id);
    }
  }, [currentScore, fetchTimeline]);

  // Track container dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight,
        });
      }
    };

    const raf = requestAnimationFrame(updateDimensions);
    window.addEventListener('resize', updateDimensions);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', updateDimensions);
    };
  }, [timeline]);

  // --- Loading / error states ---

  if (!currentScore) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-gray-400">No score selected</p>
      </div>
    );
  }

  if (currentScore.status === 'error') {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center max-w-md">
          <p className="text-red-400 text-lg font-semibold mb-2">Processing failed</p>
          <p className="text-gray-400 mb-4">
            The score &ldquo;{currentScore.title}&rdquo; could not be processed. Try re-uploading
            the file.
          </p>
          <button
            onClick={() => useScoreStore.getState().setCurrentScore(null)}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
          >
            Back to library
          </button>
        </div>
      </div>
    );
  }

  if (
    currentScore.status === 'pending' ||
    currentScore.status === 'uploading' ||
    currentScore.status === 'processing'
  ) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4" />
          <p className="text-gray-400">Processing score&hellip;</p>
          <p className="text-gray-500 text-sm mt-1">This may take a minute for large files</p>
        </div>
      </div>
    );
  }

  if (!timeline) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4" />
          <p className="text-gray-400">Loading timeline&hellip;</p>
        </div>
      </div>
    );
  }

  // --- Layout ---

  const kbHeight = showNotation ? KEYBOARD_HEIGHT_COMPACT : KEYBOARD_HEIGHT;
  const notationH = showNotation ? notationHeight : 0;
  const waterfallHeight = dimensions.height - kbHeight - notationH - CONTROLS_HEIGHT;

  return (
    <div ref={containerRef} className="flex-1 flex flex-col overflow-hidden bg-[#111111]">
      {/* Waterfall — always visible, takes remaining space */}
      <div
        className="relative flex-1"
        style={{ height: waterfallHeight > 0 ? waterfallHeight : undefined }}
      >
        <WaterfallDisplay
          timeline={timeline}
          width={dimensions.width}
          height={waterfallHeight > 0 ? waterfallHeight : WATERFALL_MIN_HEIGHT}
          config={config}
          activeNotes={activeNotes.notes}
        />

        {/* Notation toggle */}
        <button
          onClick={() => setShowNotation(!showNotation)}
          className={`absolute top-3 left-3 z-20 px-3 py-1.5 text-xs flex items-center gap-1.5 rounded-lg transition-colors ${
            showNotation
              ? 'bg-blue-600 text-white'
              : 'bg-black/60 text-gray-400 hover:text-white hover:bg-black/80'
          }`}
          title="Toggle sheet music"
        >
          <Music className="w-3.5 h-3.5" />
          Notation
        </button>
      </div>

      {/* Notation — slides in between waterfall and keyboard */}
      {showNotation && (
        <div
          data-testid="notation-panel"
          style={{ height: notationHeight, flexShrink: 0 }}
          className="relative border-t border-gray-700"
        >
          <div
            data-testid="notation-resize-handle"
            onMouseDown={onHandleMouseDown}
            title="Drag to resize notation"
            className={`absolute -top-1 left-0 right-0 h-2 z-30 cursor-row-resize ${
              isResizing ? 'bg-blue-500/40' : 'hover:bg-blue-500/20'
            }`}
          />
          <NotationDisplay
            scoreId={currentScore?.id || ''}
            traceId={currentScore?.traceId}
            musicXmlUrl={
              currentScore?.id
                ? `${import.meta.env.VITE_API_URL || '/api'}/scores/${currentScore.id}/musicxml`
                : undefined
            }
            playbackState={playbackState}
            timeline={timeline}
            width={dimensions.width}
            height={notationHeight}
            resolveSkipContext={resolveNotationSkipContext}
          />
        </div>
      )}

      {/* Piano Keyboard — directly below, notes land on it */}
      <div style={{ height: kbHeight, flexShrink: 0 }}>
        <PianoKeyboard
          expectedNotes={expectedNotes}
          expectedStaffByPitch={expectedStaffByPitch}
          playedNotes={activeNotes.notes}
          onNoteOn={(pitch) => playNote(pitch, 0.8)}
          onNoteOff={(pitch) => stopNote(pitch)}
          width={dimensions.width}
          height={kbHeight}
        />
      </div>

      {/* Playback Controls */}
      <div
        className="bg-[#1a1a1a] border-t border-gray-800 px-4 flex items-center"
        style={{ height: CONTROLS_HEIGHT, flexShrink: 0 }}
      >
        <PlaybackControls
          state={playbackState}
          totalDuration={timeline.totalDuration}
          tempo={timeline.tempo * config.tempoMultiplier}
          onSettingsClick={() => setIsSettingsOpen(true)}
          audioLoaded={audioLoaded}
          pianoLoadState={pianoLoadState}
          onPianoRetry={retryPianoLoad}
          traceId={currentScore?.traceId}
          skipContext={skipContext}
        />
      </div>

      {/* Settings Panel */}
      <PracticeSettings isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />
    </div>
  );
}
